import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share, Plus } from 'lucide-react';

/**
 * AddToHomeScreen — mobile-only flyer encouraging PWA install.
 * - Shows after 4s on first mobile visit
 * - Auto-dismisses after 9s with a countdown bar
 * - Remembers dismissal in localStorage
 * - Detects iOS vs Android for correct instructions
 */
export default function AddToHomeScreen() {
  const [show, setShow]         = useState(false);
  const [progress, setProgress] = useState(100);   // 100 → 0 over 9s
  const [platform, setPlatform] = useState('');    // 'ios' | 'android' | ''
  const timerRef                = useRef(null);
  const progressRef             = useRef(null);

  useEffect(() => {
    // Already installed as PWA
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Already dismissed
    if (localStorage.getItem('ath_dismissed_v2')) return;

    // Mobile only
    const ua = navigator.userAgent;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
    if (!isMobile) return;

    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    setPlatform(isIOS ? 'ios' : 'android');

    // Show after 4 seconds
    timerRef.current = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(timerRef.current);
  }, []);

  // Countdown bar + auto-dismiss after 9s
  useEffect(() => {
    if (!show) return;
    const start = Date.now();
    const DURATION = 9000;

    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / DURATION) * 100);
      setProgress(pct);
      if (pct <= 0) dismiss();
    }, 80);

    return () => clearInterval(progressRef.current);
  }, [show]); // eslint-disable-line

  const dismiss = () => {
    setShow(false);
    clearInterval(progressRef.current);
    localStorage.setItem('ath_dismissed_v2', '1');
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 340, damping: 32 }}
          className="fixed bottom-4 left-3 right-3 z-[10000] rounded-2xl overflow-hidden shadow-2xl"
          style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1a2540 100%)', border: '1px solid rgba(255,69,0,0.28)' }}
          data-testid="add-to-home-banner"
        >
          {/* Countdown bar */}
          <div className="h-0.5 bg-white/5 w-full">
            <motion.div
              className="h-full bg-[#FF4500]"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.08, ease: 'linear' }}
            />
          </div>

          <div className="px-4 py-3 flex items-center gap-3">
            {/* Icon */}
            <div className="w-10 h-10 rounded-xl bg-[#FF4500]/15 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-[#FF4500]" stroke="currentColor" strokeWidth={2}>
                <path d="M12 19V5M5 12l7-7 7 7"/>
              </svg>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white leading-tight" style={{ fontFamily: 'Space Grotesk' }}>
                Add Fly'N Out to your home screen
              </p>
              {platform === 'ios' ? (
                <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1 flex-wrap">
                  Tap
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white/10 text-white">
                    <Share className="w-3 h-3" /> Share
                  </span>
                  then
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white/10 text-white">
                    <Plus className="w-3 h-3" /> Add to Home Screen
                  </span>
                </p>
              ) : (
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Tap the menu (⋮) then "Add to Home screen" for instant access
                </p>
              )}
            </div>

            {/* Dismiss */}
            <button
              onClick={dismiss}
              className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
              data-testid="add-to-home-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
